/* Download optional controls only when the application opens their surface. */
((root, document) => {
  "use strict";

  const owner = document.currentScript;
  const definitions = Object.freeze({
    business: { file: "business.js", global: "LouderrBusiness", methods: ["create"], label: "Business tools" },
    uploads: { file: "uploads.js", global: "LobbyUploads", methods: ["createUploadManager"], label: "Upload controls" },
    emojis: { file: "chat-emojis.js", global: "LoudErrChatEmojis", methods: ["get", "search"], label: "Emoji picker" },
  });
  const sources = new Map(Object.entries(definitions).map(([name, feature]) => [
    name, owner?.getAttribute(`data-${name}-src`) || `/static/${feature.file}`,
  ]));
  const pending = new Map();
  const failed = new Set();
  const loadTimeoutMs = 20000;

  function definition(name) {
    return Object.prototype.hasOwnProperty.call(definitions, name) ? definitions[name] : null;
  }

  function peek(name) {
    const feature = definition(name);
    const value = feature && root[feature.global];
    return value && feature.methods.every((method) => typeof value[method] === "function") ? value : null;
  }

  function status(name) {
    if (!definition(name)) return "unavailable";
    if (peek(name)) return "ready";
    if (pending.has(name)) return "loading";
    return failed.has(name) ? "error" : "idle";
  }

  function load(name) {
    const feature = definition(name);
    if (!feature) return Promise.reject(new Error("These controls are unavailable."));
    const existing = peek(name);
    if (existing) return Promise.resolve(existing);
    if (pending.has(name)) return pending.get(name);

    let resolve, reject;
    const request = new Promise((yes, no) => { resolve = yes; reject = no; });
    pending.set(name, request);
    failed.delete(name);
    let script, timer;
    const finish = (error) => {
      // A browser may deliver more than one terminal event for a removed node.
      if (pending.get(name) !== request) return;
      pending.delete(name);
      if (timer !== undefined) root.clearTimeout(timer);
      if (script) { script.onload = null; script.onerror = null; }
      const value = !error && peek(name);
      if (!value) {
        failed.add(name);
        script?.remove();
        reject(new Error(`${feature.label} could not be loaded. Try again.`));
      } else {
        failed.delete(name);
        resolve(value);
      }
    };

    try {
      const url = new URL(sources.get(name), root.location.href);
      // Never turn a module name or a document attribute into an external script URL.
      if (url.origin !== root.location.origin || url.pathname !== `/static/${feature.file}`
          || url.username || url.password || url.hash || !/^(?:\?v=[a-zA-Z0-9._-]{1,64})?$/.test(url.search)) {
        throw new Error("Invalid optional script URL.");
      }
      script = document.createElement("script");
      script.async = true;
      script.src = url.href;
      script.dataset.optionalFeature = name;
      if (owner?.nonce) script.nonce = owner.nonce;
      script.onload = () => finish(false);
      script.onerror = () => finish(true);
      // A stalled connection may emit neither load nor error. Release every
      // waiting caller so the existing error UI can offer an explicit retry.
      timer = root.setTimeout(() => finish(true), loadTimeoutMs);
      document.head.append(script);
    } catch (_) { finish(true); }
    return request;
  }

  root.LouderrOptional = Object.freeze({ load, peek, status });
})(window, document);
